/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Spin,
  Row,
  Col,
  Modal,
  TreeSelect,
  Button,
  Empty,
} from 'antd';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { checkToken } from 'api/utils';
import { areaBuscar, normalizeData } from 'utils/normalizers';
import { formatReceived } from 'utils/formatValues';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import {
  getEstadosGlobales,
  getAreas,
  getContentTypesUR,
  getCuentasContables,
} from 'api/catalogos/index';
import Select from 'components/Select';
import makeTree from 'utils/makeTreeFromCuentasContables';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

import {
  getCuentasContablesPasivoPatronalISSSTE,
  getCuentaContablePasivoPatronalISSSTE,
  postCuentaContablePasivoPatronalISSSTE,
  patchCuentaContablePasivoPatronalISSSTE,
  deleteCuentaContablePasivoPatronalISSSTE,
} from 'api/catalogos/CuentasContablesPasivoPatronalISSSTE';
import {
  getCuentasContablesPasivoEmpleadoISSSTE,
  getCuentaContablePasivoEmpleadoISSSTE,
  postCuentaContablePasivoEmpleadoISSSTE,
  patchCuentaContablePasivoEmpleadoISSSTE,
  deleteCuentaContablePasivoEmpleadoISSSTE,
} from 'api/catalogos/CuentasContablesPasivoEmpleadoISSSTE';

// Mapea el tipo (patronal | empleado) a su juego de funciones de API.
const API_POR_TIPO = {
  patronal: {
    getAll: getCuentasContablesPasivoPatronalISSSTE,
    getOne: getCuentaContablePasivoPatronalISSSTE,
    post: postCuentaContablePasivoPatronalISSSTE,
    patch: patchCuentaContablePasivoPatronalISSSTE,
    remove: deleteCuentaContablePasivoPatronalISSSTE,
  },
  empleado: {
    getAll: getCuentasContablesPasivoEmpleadoISSSTE,
    getOne: getCuentaContablePasivoEmpleadoISSSTE,
    post: postCuentaContablePasivoEmpleadoISSSTE,
    patch: patchCuentaContablePasivoEmpleadoISSSTE,
    remove: deleteCuentaContablePasivoEmpleadoISSSTE,
  },
};

const requiredRule = {
  required: true,
  message: 'El campo es requerido',
};

const rules = {
  required: [requiredRule],
  estatus: [requiredRule],
};

/**
 * Grid "Cuenta Contable por Área" para un Seguro ISSSTE.
 * Es un maestro-detalle: solo funciona cuando el Seguro ISSSTE padre ya existe
 * (necesita `seguro_issste`). En alta, aún no hay id de padre y se muestra un aviso.
 *
 * @param {number}  seguroIssteId  Id del Seguro ISSSTE padre.
 * @param {'patronal'|'empleado'} tipo  Qué endpoint usar.
 * @param {object}  permission
 * @param {boolean} disableEditar  Deshabilita altas/ediciones (p.ej. Autorizado).
 */
const SeguroISSSTECuentasContablesPorArea = ({
  seguroIssteId,
  tipo = 'patronal',
  permission,
  disableEditar = false,
}) => {
  const api = API_POR_TIPO[tipo] || API_POR_TIPO.patronal;
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [areas, setAreas] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [estados, setEstados] = useState([]);

  const formInstances = [form];

  const normalizeCuentasContables = (_data) => {
    if (_data) {
      return _data.map((item) => {
        const clone = { ...item };
        const children = normalizeCuentasContables(clone.children);
        const hasCuentasChildren = children.some((e) => e.conac);
        const selectable = clone.tipo !== 7 && !hasCuentasChildren;
        if (!children.length) {
          delete clone.children;
          return ({
            ...clone,
            title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
            value: clone.id,
            key: clone.id,
            disabled: !selectable,
          });
        }
        return ({
          ...clone,
          title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
          value: clone.id,
          key: clone.id,
          children,
          disabled: !selectable,
        });
      });
    }
    return [];
  };

  const flattenWithChildren = (arr) => {
    const flattened = [];
    const traverse = (items) => {
      items.forEach((item) => {
        const { children, ...itemWithoutChildren } = item;
        flattened.push(itemWithoutChildren);
        if (children && children.length > 0) {
          traverse(children);
        }
      });
    };
    traverse(arr);
    return flattened;
  };

  const fetchData = async () => {
    if (!seguroIssteId) {
      setData([]);
      return;
    }
    setLoading(true);
    const _data = await api.getAll({ seguro_issste: seguroIssteId });
    setData(formatReceived(_data?.filter((e) => e.seguro_issste === seguroIssteId)));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (!seguroIssteId) {
      setData([]);
      return undefined;
    }
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _cuentasContables,
        _estadosGlobales,
        _areas,
        _contentTypes,
        _data,
      ] = await Promise.all([
        getCuentasContables(),
        getEstadosGlobales(),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getContentTypesUR(),
        api.getAll({ seguro_issste: seguroIssteId }),
      ]);
      const formattedURS = normalizeData(_areas);
      if (mounted) {
        const normalizedCuentas = normalizeCuentasContables(makeTree(_cuentasContables));
        setCuentasContables(flattenWithChildren(normalizedCuentas));
        setAreas(formattedURS);
        setContentTypes(_contentTypes);
        setData(formatReceived(_data?.filter((e) => e.seguro_issste === seguroIssteId)));
        setEstados(_estadosGlobales?.filter((e) => e.id === 1 || e.id === 3));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [seguroIssteId, tipo]);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    const _cuentaContable = await api.getOne(match.id);
    const areaValue = areaBuscar(_cuentaContable?.area_content_id, areas);
    _cuentaContable.unidad_responsable = areaValue?.value;
    setTimeout(() => {
      if (_cuentaContable) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_cuentaContable));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const getURValuesToSend = () => {
    const unidad_responsable = form.getFieldValue('unidad_responsable');
    if (unidad_responsable) {
      const [_area, _nivel] = unidad_responsable.split('/');
      const area_content_id = contentTypes.find((ct) => ct.nivel.toString() === _nivel)?.id;
      return { _area, area_content_id };
    }
    return { _area: undefined, area_content_id: undefined };
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const contentValues = getURValuesToSend();
      const values = {
        ...form.getFieldsValue(),
        // `area` es el ContentType; `area_content_id` es el id del área real.
        area: contentValues?.area_content_id,
        area_content_id: contentValues?._area,
        seguro_issste: seguroIssteId,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? api.patch(key, values)
        : api.post(values));
      if (saved) {
        if (_continue) {
          await fetchData();
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await api.remove(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Área',
      dataIndex: 'area_content_id',
      key: 'area_content_id',
      width: 200,
      render: (val) => areaBuscar(val, areas)?.title,
    },
    {
      titleText: 'Cuenta Contable',
      dataIndex: 'cuenta_contable',
      key: 'cuenta_contable',
      width: 300,
      render: (val) => {
        const _cuentaContable = cuentasContables.find((e) => e.id === val);
        if (!_cuentaContable) return '';
        return `${_cuentaContable?.clave_visible || _cuentaContable?.clave} - ${_cuentaContable?.nombre}`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  if (!seguroIssteId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Guarde primero el Seguro ISSSTE para configurar las cuentas contables por Área."
      />
    );
  }

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd: () => onClickAdd(),
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => onClickDelete(),
          }}
          permission={permission}
          disableAdd={disableEditar}
          disableDelete={disableEditar}
          allowExportPDF={false}
        />
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Cuenta Contable por Área"
          loading={loading}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cuenta Contable por Área`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableEditar}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrolltofirsterror
            name="seguroIsssteCuentaPorArea"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="unidad_responsable"
                  label="Área"
                  rules={rules.required}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={areas}
                    disabled={disableEditar}
                  />
                </Form.Item>
                <Form.Item
                  name="cuenta_contable"
                  label="Cuenta Contable"
                  rules={rules.required}
                >
                  <Select
                    keyProp="clave"
                    labelProp="nombre"
                    keyLabelRender
                    dataSource={cuentasContables}
                    disabled={disableEditar}
                  />
                </Form.Item>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableEditar}
                  />
                </Form.Item>
              </Col>
              <Form.Item>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default SeguroISSSTECuentasContablesPorArea;
